import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import ProductItem from './ProductItem';
import { Empty } from 'antd';
import { ProductData } from 'types';
import { InventoryDocumentItem, ProductVersion } from 'models';
import { getFilePath } from 'utils';
import { ButtonCommon } from 'components/common';
import { MagnifyingGlass, SpinnerGap, Trash } from '@phosphor-icons/react';
// import { ProductSelectItem } from 'components/Product';
import { useProductImagesMap } from 'hooks/useProductImagesMap';
import ProductSearchInput from 'components/common/Input/ProductSearchInput';
import ProductSearchModal from 'components/common/Modal/ProductSearchModal';

interface ListProductProps {
  // Existing props
  products: InventoryDocumentItem[];
  onSelectProduct?: (product: ProductData) => void;
  disabled?: boolean;
  emptyText?: string;
  selectedProductId?: string;
  isQuickView?: boolean;
  title?: string;

  // New props
  availableProductVersions?: ProductVersion[];
  onSelectProductToList?: (product: ProductVersion) => void;
  onRemoveProduct?: (productId: string) => void;
  selectedProducts?: ProductVersion[];
  isLoadingProducts?: boolean;
  maxItems?: number;
  minItems?: number;
  onProductSearch?: (searchTerm: string) => void;
  onSelectAll?: () => void;
  selectionType?: 'basic' | 'advanced';
  onConfirmAdvancedSelect?: (products: ProductVersion[]) => void;
}

const ListProduct: React.FC<ListProductProps> = ({
  products,
  onSelectProduct,
  emptyText = 'Không có sản phẩm nào',
  selectedProductId,
  isQuickView = false,
  title = 'DANH SÁCH SẢN PHẨM ĐÃ CHỌN',

  availableProductVersions = [],
  onSelectProductToList,
  onRemoveProduct,
  selectedProducts = [],
  isLoadingProducts = false,
  onProductSearch,
  selectionType = 'basic',
  onConfirmAdvancedSelect,
}) => {
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const productVersionIds = products ? (products.map((item) => item.productVersionId).filter(Boolean) as string[]) : [];
  // const [productItems, setProductItems] = useState<ProductData[]>([]);
  const { imagesMap: productImagesMap } = useProductImagesMap(productVersionIds);
  const getImageUrl = (productVersionId: string) => {
    const imagePaths = productImagesMap?.[productVersionId];
    return imagePaths && imagePaths.length > 0 ? getFilePath() + '/' + imagePaths[0] : undefined;
  };
  // Convert ProductVersion[] to ProductData[] for display
  const mapProductVersionsToProductData = useCallback(
    (versions: ProductVersion[]): ProductData[] => {
      return versions.map((version) => ({
        id: version.id || '',
        name: version.name || '',
        code: version.product?.code || undefined,
        imageUrl: version.productImages?.[0]?.image?.filePath
          ? getFilePath() + '/' + version.productImages[0].image.filePath
          : undefined,
        unitPrice: version.costPrice || 0,
        category: version.category?.name || version.categoryId || 'Chưa phân loại',
        brand: version.brand?.name || version.brandId || 'Chưa có thương hiệu',
        quantity: version.product?.quantity || version?.quantity || 0,
        actualQuantity: version.actualQuantity || undefined,
        unit: version.baseUnit?.name || 'Cái',
        currencyUnit: version.currencyUnit?.name || 'VND',
        reason: version.reason || undefined,
        color: version.color || undefined,
        sizeSpec: version.sizeSpec || undefined,
        dimension: version.dimension || undefined,
      }));
    },
    [productImagesMap],
  );

  // Convert InventoryDocumentItem[] to ProductData[]
  const mapInventoryItemsToProductData = useCallback(
    (items: InventoryDocumentItem[]): ProductData[] => {
      return items.map((item) => ({
        id: item.id || '',
        code: item?.productVersion?.product?.code || undefined,
        name: item.name || item?.productVersion?.name || '',
        imageUrl: getImageUrl(item.productVersionId || ''),
        unitPrice: item?.costPrice || 0,
        category: item.category || item?.productVersion?.category?.name || 'Chưa phân loại',
        brand: item.brand || item?.productVersion?.brand?.name || 'Chưa có thương hiệu',
        quantity: item.quantity || 0,
        actualQuantity: item.actualQuantity,
        unit: item.unit || item?.productVersion?.baseUnit?.name || 'Cái',
        currencyUnit: item?.productVersion?.currencyUnit?.name || 'VND',
        color: item?.color || undefined,
        sizeSpec: item?.sizeSpec || undefined,
        dimension: item?.productDimension || undefined,
      }));
    },
    [productImagesMap],
  );

  // Update productItems when selectedProducts or products change
  const productItems = useMemo(() => {
    if (selectedProducts.length > 0) {
      return mapProductVersionsToProductData(selectedProducts);
    } else if (products && products.length > 0) {
      return mapInventoryItemsToProductData(products);
    } else {
      return [];
    }
  }, [selectedProducts, products, mapProductVersionsToProductData, mapInventoryItemsToProductData]);
  // Handle product selection from dropdown
  const handleSelect = (selectedOption: { value: string; label: ReactNode }) => {
    const productId = selectedOption.value;

    if (selectedProducts.find((p) => p.id === productId)) return;

    const product = availableProductVersions.find((p) => p.id === productId);
    if (product && onSelectProductToList) {
      onSelectProductToList(product);
    }
  };

  // Handle product removal
  const handleRemove = (productId: string) => {
    if (onRemoveProduct) {
      onRemoveProduct(productId);
    }
  };

  const handleSearch = (searchTerm: string) => {
    if (onProductSearch) {
      onProductSearch(searchTerm);
    }
  };

  const handleAdvancedConfirm = (productsSelected: ProductVersion[]) => {
    if (onConfirmAdvancedSelect) {
      onConfirmAdvancedSelect(productsSelected);
      return;
    }

    if (onSelectProductToList) {
      productsSelected
        .filter((product) => !selectedProducts.some((selected) => selected.id === product.id))
        .forEach((product) => onSelectProductToList(product));
    }
  };

  // Filter available products to exclude already selected ones
  const filteredAvailableProducts = availableProductVersions.filter(
    (p) => !selectedProducts.some((sp) => sp.id === p.id),
  );

  // Show empty state
  if (productItems.length === 0) {
    return (
      <div className='flex flex-col h-full min-h-[calc(100vh-200px)]'>
        {!isQuickView && (
          <div className='flex w-full justify-between items-center mb-4'>
            <div className='text-lg font-bold text-[var(--textBlack)] flex'>{title}</div>
            <div className='flex items-center gap-3'>
              {selectionType === 'advanced' && (
                <ButtonCommon color='none' size='sm' radius='rounded' onClick={() => setIsAdvancedModalOpen(true)}>
                  Tìm kiếm nâng cao
                </ButtonCommon>
              )}
              {onSelectProductToList && onProductSearch && (
                <ProductSearchInput
                  placeholder='Tìm kiếm và chọn sản phẩm...'
                  options={filteredAvailableProducts}
                  onSelect={handleSelect}
                  onSearch={handleSearch}
                  loading={isLoadingProducts}
                  value={selectedProducts.map((p) => ({
                    value: p.id || '',
                    label: p.name || '',
                  }))}
                  suffixIcon={<MagnifyingGlass size={18} weight='bold' color='var(--textBlack56)' />}
                  notFoundContent={
                    isLoadingProducts ? (
                      <div className='flex justify-start items-center'>
                        <SpinnerGap size={18} weight='bold' className='text-[var(--primaryColor500)] animate-spin' />
                        <span className='ml-2'>Đang tìm kiếm...</span>
                      </div>
                    ) : (
                      'Không tìm thấy sản phẩm'
                    )
                  }
                  className='max-w-[50%] min-w-[500px] !h-[32px] !border-none'
                />
              )}
            </div>
          </div>
        )}
        <Empty description={emptyText} className='my-4' />
        {selectionType === 'advanced' && (
          <ProductSearchModal
            isOpen={isAdvancedModalOpen}
            onClose={() => setIsAdvancedModalOpen(false)}
            onConfirm={handleAdvancedConfirm}
            selectedProducts={selectedProducts}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={
        `flex flex-col ${!isQuickView && selectionType !== 'advanced' ? 'p-4' : ''} w-full min-h-[calc(100vh-190px)] items-center ` +
        (!isQuickView ? 'bg-white rounded-[16px]' : 'bg-[var(--bgGreyColor)] rounded-b-lg')
      }
    >
      {!isQuickView && (
        <div className='flex w-full justify-between items-center mb-4'>
          <div className='text-lg font-bold text-[var(--textBlack)] flex'>{title}</div>
          <div className='flex items-center gap-3'>
            {selectionType === 'advanced' && (
              <ButtonCommon color='none' size='sm' radius='rounded' onClick={() => setIsAdvancedModalOpen(true)}>
                Tìm kiếm nâng cao
              </ButtonCommon>
            )}
            {onSelectProductToList && onProductSearch && (
              <ProductSearchInput
                placeholder='Tìm kiếm và chọn sản phẩm...'
                options={filteredAvailableProducts}
                onSelect={handleSelect}
                onSearch={handleSearch}
                loading={isLoadingProducts}
                value={selectedProducts.map((p) => ({
                  value: p.id || '',
                  label: p.name || '',
                }))}
                suffixIcon={<MagnifyingGlass size={18} weight='bold' color='var(--textBlack56)' />}
                notFoundContent={
                  isLoadingProducts ? (
                    <div className='flex justify-start items-center'>
                      <SpinnerGap size={18} weight='bold' className='text-[var(--primaryColor500)] animate-spin' />
                      <span className='ml-2'>Đang tìm kiếm...</span>
                    </div>
                  ) : (
                    'Không tìm thấy sản phẩm'
                  )
                }
                className='max-w-[50%] min-w-[500px] !h-[32px] !border-none'
              />
            )}
          </div>
        </div>
      )}

      <div className='flex flex-col gap-2 overflow-y-auto w-full max-w-[90vw] max-h-[calc(100vh-250px)]'>
        {productItems.map((product) => (
          <div key={product.id} className='relative'>
            <ProductItem
              id={product.id}
              name={
                product.name +
                (product.color ? ` - ${product.color}` : '') +
                (product.dimension ? ` - ${product.dimension}` : '') +
                (product.sizeSpec ? ` - ${product.sizeSpec}` : '') +
                (product.code ? ` - ${product.code}` : '')
              }
              code={product.code}
              imageUrl={product.imageUrl}
              unitPrice={product.unitPrice}
              category={product.category}
              quantity={product.quantity}
              actualQuantity={product.actualQuantity}
              unit={product.unit}
              brand={product.brand}
              currencyUnit={product.currencyUnit}
              onSelectProduct={onSelectProduct}
              isSelected={selectedProductId === product.id}
              isQuickView={isQuickView}
              isDisplayTotal={onSelectProductToList ? false : true}
              isDisplaySubtotal={onSelectProductToList ? false : true}
              reason={product.reason}
            />
            {/* Remove button */}
            {onRemoveProduct && (
              <button
                type='button'
                onClick={() => handleRemove(product.id)}
                className='absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1 shadow text-xs cursor-pointer z-10'
                aria-label='Xóa sản phẩm'
              >
                <Trash size={16} color='var(--sematicErrorColor500)' />
              </button>
            )}
          </div>
        ))}
      </div>

      {selectionType === 'advanced' && (
        <ProductSearchModal
          isOpen={isAdvancedModalOpen}
          onClose={() => setIsAdvancedModalOpen(false)}
          onConfirm={handleAdvancedConfirm}
          selectedProducts={selectedProducts}
        />
      )}
    </div>
  );
};

export default ListProduct;
